import * as Sentry from '@sentry/react';
import { logErrorWithContext, trackOperation, trackPerformance } from './sentrySetup';
import { gatewayFetch } from '../../helpers/gateway';

interface TrackedFetchOptions extends RequestInit {
	/** Optional operation name override for tracking */
	operationName?: string;
}

/**
 * Extract a readable operation name from a URL
 */
const extractOperationName = (url: string): string => {
	try {
		const urlObj = new URL(url, window.location.origin);
		const pathname = urlObj.pathname;
		// Get the last 2-3 meaningful segments
		const segments = pathname.split('/').filter(Boolean);
		const relevantSegments = segments.slice(-3);
		return relevantSegments.join('/') || 'unknown';
	} catch {
		return 'unknown';
	}
};

/**
 * Tracked fetch wrapper that automatically adds Sentry breadcrumbs and error tracking
 */
export const trackedFetch = async (url: string, options: TrackedFetchOptions = {}): Promise<Response> => {
	const { operationName, ...fetchOptions } = options;
	const operation = operationName || extractOperationName(url);
	const method = fetchOptions.method || 'GET';
	const startTime = Date.now();

	// Add breadcrumb for request start
	Sentry.addBreadcrumb({
		category: 'api',
		message: `${method} ${operation}`,
		level: 'info',
		data: {
			url,
			method,
			event: 'request_start',
		},
	});

	try {
		const response = await gatewayFetch(url, fetchOptions);
		const duration = Date.now() - startTime;

		// Track response
		const isSuccess = response.status >= 200 && response.status < 400;

		Sentry.addBreadcrumb({
			category: 'api',
			message: `${method} ${operation}: ${response.status}`,
			level: isSuccess ? 'info' : 'warning',
			data: {
				url,
				method,
				status: response.status,
				duration_ms: duration,
				event: 'request_complete',
			},
		});

		// Track slow operations
		trackPerformance(`api_${operation}`, { duration, success: isSuccess });

		// Log errors for 400+ status codes
		if (response.status >= 400) {
			trackOperation(`api_error_${operation}`, 'failure', {
				status: response.status,
				url,
				method,
				duration_ms: duration,
			});
		}

		return response;
	} catch (error) {
		const duration = Date.now() - startTime;

		// Log network/fetch errors
		logErrorWithContext(error, `api_fetch_error`, {
			operation,
			url,
			method,
			duration_ms: duration,
		});

		throw error;
	}
};

/**
 * Create a tracked fetch with retry support by composing with fetch-retry library.
 * This properly leverages the fetch-retry library's retry behavior instead of reimplementing it.
 */
export const createTrackedFetchRetry = (
	fetchRetryBuilder: (baseFetch: typeof fetch, defaults?: object) => typeof fetch,
	retryConfig: { retryCount: number; retryDelay: number },
) => {
	// Create a retry-enabled version of trackedFetch
	return fetchRetryBuilder((trackedFetch as unknown) as typeof fetch, retryConfig);
};

export default trackedFetch;
